/**
 * MeshEraser - Interactive triangle eraser for 3D meshes.
 *
 * Lets the user paint on the mesh surface to erase (hide) triangles.
 * Each mouse-down→drag→mouse-up creates a separate named "sequence"
 * that can be individually toggled, renamed and deleted.
 * Ctrl+Z removes the last sequence while in erasing mode.
 *
 * Supports both single THREE.Mesh targets and THREE.Group targets
 * (e.g. OpenFOAM cases with multiple boundary patches).
 */
class MeshEraser {
  constructor(viewer, filters) {
    this.viewer = viewer;
    this.filters = filters;
    this.engine = viewer.engine;
    this.controls = viewer.engine.controls;

    // Master state
    this.enabled = false;
    this.erasing = false;   // erasing mode active (button toggled)
    this.isMouseDown = false;

    // Brush
    this.brushRadiusPercent = 5; // % of bbox diagonal
    this.brushRadius = 0.05;    // world units (recomputed on enable)
    this.brushCursor = null;
    this.brushShape = 'sphere'; // 'sphere' or 'cube'

    // Sequences – each mouse stroke = one sequence
    this.sequences = [];
    this.activeSequenceId = null;
    this.sequenceIdCounter = 0;

    // Original index buffer backup(s)
    this.originalIndices = null;       // Uint32Array – single mesh
    this.originalIndicesMap = new Map(); // meshKey → Uint32Array – for Group meshes
    this.targetMesh = null;

    // Spatial hash grid for fast triangle lookup
    this.spatialGrid = null;          // single mesh grid
    this.spatialGrids = new Map();    // meshKey → Map<cellKey, triIndex[]> – for Group meshes
    this.gridCellSize = 0;

    // Throttle: schedule index rebuild via rAF so we never rebuild more than once per frame
    this._rebuildScheduled = false;
    this._rebuildDirty = false;

    // Throttle: raycast at most once per animation frame
    this._mouseMoveScheduled = false;

    // Colour palette
    this.colors = [
      '#E24A4A', '#4A90D9', '#50C878', '#FFD700',
      '#FF69B4', '#9B59B6', '#1ABC9C', '#E67E22'
    ];

    // Viewport element for cursor style
    this.viewport = document.getElementById('viewport');

    // Bound event handlers (for attach/detach)
    this._onMouseMove = this._handleMouseMove.bind(this);
    this._onMouseDown = this._handleMouseDown.bind(this);
    this._onMouseUp = this._handleMouseUp.bind(this);
    this._onKeyDown = this._handleKeyDown.bind(this);

    // Mouse position (NDC)
    this._mouse = new THREE.Vector2();

    // Last known hit distance — used to keep the brush cursor visible when
    // the raycast misses (e.g. after erasing the triangles under the cursor).
    this._lastHitDistance = 0;

    this._initBrushCursor();
  }

  // ---------- Enable / Disable ----------

  enable() {
    this.enabled = true;
    this._captureTargetMesh();
    this._computeBrushRadius();
    this._buildSpatialGrid();
    console.log('[MeshEraser] Enabled. targetMesh:', this.targetMesh?.type,
      'originalIndices:', this.originalIndices?.length,
      'originalIndicesMap size:', this.originalIndicesMap.size,
      'brushRadius:', this.brushRadius,
      'spatialGrid cells:', this.spatialGrid?.size,
      'spatialGrids count:', this.spatialGrids.size);
  }

  disable() {
    if (this.erasing) this.stopErasing();
    this.enabled = false;
  }

  // ---------- Target mesh ----------

  _captureTargetMesh() {
    const id = this.viewer.meshParts?.activeMeshObjectId || 'grid-surface';
    let entry = this.engine.objects.get(id);
    if (!entry) entry = this.engine.objects.get('main-mesh');
    if (!entry) {
      for (const [, e] of this.engine.objects) {
        if (e.object && (e.object.isMesh || e.object.type === 'Group')) {
          entry = e;
          break;
        }
      }
    }
    if (!entry) return;

    const obj = entry.object;
    this.targetMesh = obj;

    if (obj.isMesh && obj.geometry) {
      this._backupIndices(obj);
    } else if (obj.type === 'Group') {
      obj.traverse(child => {
        if (child.isMesh && child.geometry) {
          this._backupGroupChildIndices(child);
        }
      });
    }
  }

  _backupIndices(mesh) {
    // Don't overwrite existing backup (prevents capturing already-modified indices)
    if (this.originalIndices) return;
    const geo = mesh.geometry;
    if (geo.index) {
      this.originalIndices = new Uint32Array(geo.index.array);
    } else {
      // Non-indexed geometry (e.g. STL files) — create a sequential index buffer
      const vertexCount = geo.getAttribute('position').count;
      const indices = new Uint32Array(vertexCount);
      for (let i = 0; i < vertexCount; i++) indices[i] = i;
      geo.setIndex(new THREE.BufferAttribute(indices, 1));
      this.originalIndices = new Uint32Array(indices);
      console.log(`[MeshEraser] Created index buffer for non-indexed geometry (${vertexCount / 3} triangles)`);
    }
  }

  _backupGroupChildIndices(mesh) {
    const geo = mesh.geometry;
    const key = mesh.name || mesh.uuid;
    // Don't overwrite existing backup (prevents capturing already-modified indices)
    if (this.originalIndicesMap.has(key)) return;
    if (geo.index) {
      this.originalIndicesMap.set(key, new Uint32Array(geo.index.array));
    } else {
      const vertexCount = geo.getAttribute('position').count;
      const indices = new Uint32Array(vertexCount);
      for (let i = 0; i < vertexCount; i++) indices[i] = i;
      geo.setIndex(new THREE.BufferAttribute(indices, 1));
      this.originalIndicesMap.set(key, new Uint32Array(indices));
    }
  }

  // ---------- Spatial hash grid ----------

  _buildSpatialGrid() {
    if (this._isGroupMode()) {
      this._buildSpatialGridMulti();
      return;
    }
    // Single mesh mode
    const mesh = this._getSingleMesh();
    if (!mesh || !mesh.geometry || !mesh.geometry.index) return;

    const geo = mesh.geometry;
    const pos = geo.getAttribute('position');
    const idx = this.originalIndices || geo.index.array;

    geo.computeBoundingBox();
    const box = geo.boundingBox;
    const diag = new THREE.Vector3().subVectors(box.max, box.min).length();
    this.gridCellSize = Math.max(this.brushRadius * 2, diag / 100);

    const grid = new Map();
    const totalTris = idx.length / 3;

    for (let t = 0; t < totalTris; t++) {
      const base = t * 3;
      const a = idx[base], b = idx[base + 1], c = idx[base + 2];
      const cx = (pos.getX(a) + pos.getX(b) + pos.getX(c)) / 3;
      const cy = (pos.getY(a) + pos.getY(b) + pos.getY(c)) / 3;
      const cz = (pos.getZ(a) + pos.getZ(b) + pos.getZ(c)) / 3;
      const key = this._gridKey(cx, cy, cz);
      if (!grid.has(key)) grid.set(key, []);
      grid.get(key).push(t);
    }

    this.spatialGrid = grid;
  }

  _buildSpatialGridMulti() {
    const meshes = this._getAllChildMeshes();
    if (meshes.length === 0) return;

    // Compute cell size from overall bounding box
    const box = new THREE.Box3().setFromObject(this.targetMesh);
    if (box.isEmpty()) return;
    const diag = new THREE.Vector3().subVectors(box.max, box.min).length();
    this.gridCellSize = Math.max(this.brushRadius * 2, diag / 100);

    this.spatialGrids = new Map();
    for (const mesh of meshes) {
      const meshKey = this._getMeshKey(mesh);
      const indices = this._getOriginalIndices(mesh);
      if (!indices || !mesh.geometry) continue;

      const pos = mesh.geometry.getAttribute('position');
      const grid = new Map();
      const totalTris = indices.length / 3;

      for (let t = 0; t < totalTris; t++) {
        const base = t * 3;
        const a = indices[base], b = indices[base + 1], c = indices[base + 2];
        const cx = (pos.getX(a) + pos.getX(b) + pos.getX(c)) / 3;
        const cy = (pos.getY(a) + pos.getY(b) + pos.getY(c)) / 3;
        const cz = (pos.getZ(a) + pos.getZ(b) + pos.getZ(c)) / 3;
        const cellKey = this._gridKey(cx, cy, cz);
        if (!grid.has(cellKey)) grid.set(cellKey, []);
        grid.get(cellKey).push(t);
      }

      this.spatialGrids.set(meshKey, grid);
    }
  }

  _gridKey(x, y, z) {
    const s = this.gridCellSize;
    return `${Math.floor(x / s)},${Math.floor(y / s)},${Math.floor(z / s)}`;
  }

  _getNearbyCells(px, py, pz, grid) {
    const g = grid || this.spatialGrid;
    if (!g) return [];
    const s = this.gridCellSize;
    const r = this.brushRadius;
    const cells = Math.ceil(r / s);
    const bx = Math.floor(px / s);
    const by = Math.floor(py / s);
    const bz = Math.floor(pz / s);
    const result = [];
    for (let dx = -cells; dx <= cells; dx++) {
      for (let dy = -cells; dy <= cells; dy++) {
        for (let dz = -cells; dz <= cells; dz++) {
          const key = `${bx + dx},${by + dy},${bz + dz}`;
          const tris = g.get(key);
          if (tris) {
            for (let i = 0; i < tris.length; i++) result.push(tris[i]);
          }
        }
      }
    }
    return result;
  }

  // ---------- Brush radius ----------

  _computeBrushRadius() {
    if (!this.targetMesh) return;
    // Use setFromObject to handle both single Mesh and Group targets
    const box = new THREE.Box3().setFromObject(this.targetMesh);
    if (box.isEmpty()) return;
    const diag = new THREE.Vector3().subVectors(box.max, box.min).length();
    this.brushRadius = (this.brushRadiusPercent / 100) * diag;
    // If cursor is already visible (e.g. slider changed while erasing),
    // animate toward the new size. Otherwise _showBrushCursor handles it.
    if (this.brushCursor && this.brushCursor.visible) {
      this._startScaleTransition();
    }
  }

  setBrushRadius(percent) {
    this.brushRadiusPercent = percent;
    this._computeBrushRadius();
    if (this.enabled) this._debouncedBuildSpatialGrid();
  }

  _debouncedBuildSpatialGrid() {
    if (this._gridRebuildTimer) clearTimeout(this._gridRebuildTimer);
    this._gridRebuildTimer = setTimeout(() => {
      this._gridRebuildTimer = null;
      this._buildSpatialGrid();
    }, 150);
  }

  // ---------- Brush cursor ----------

  _initBrushCursor() {
    this._recreateBrushCursor();
  }

  _recreateBrushCursor() {
    if (this.brushCursor) {
      this.engine.scene.remove(this.brushCursor);
      this.brushCursor.geometry.dispose();
      this.brushCursor.material.dispose();
    }
    const geo = this.brushShape === 'cube'
      ? new THREE.BoxGeometry(2, 2, 2)
      : new THREE.SphereGeometry(1, 24, 16);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff4444,
      wireframe: true,
      transparent: true,
      opacity: 0.35,
      depthTest: false
    });
    this.brushCursor = new THREE.Mesh(geo, mat);
    this.brushCursor.visible = false;
    this.brushCursor.renderOrder = 999;
    this.brushCursor.name = 'EraserBrushCursor';
    this.engine.scene.add(this.brushCursor);
  }

  setBrushShape(shape) {
    this.brushShape = shape;
    this._recreateBrushCursor();
  }

  _showBrushCursor(worldPos) {
    this.brushCursor.position.copy(worldPos);
    const target = this.brushRadius;
    if (!this.brushCursor.visible) {
      // First appearance — snap to target immediately
      this.brushCursor.scale.setScalar(target);
    } else {
      // Smooth lerp toward target
      const current = this.brushCursor.scale.x;
      this.brushCursor.scale.setScalar(current + (target - current) * 0.18);
    }
    this.brushCursor.visible = true;
  }

  /**
   * Runs a short rAF loop to smoothly transition the brush cursor scale
   * when the radius changes while the cursor is already visible (e.g. slider drag).
   */
  _startScaleTransition() {
    if (this._scaleTransitionId) return; // already running
    const animate = () => {
      if (!this.brushCursor || !this.brushCursor.visible) {
        this._scaleTransitionId = null;
        return;
      }
      const current = this.brushCursor.scale.x;
      const target = this.brushRadius;
      const newScale = current + (target - current) * 0.18;
      this.brushCursor.scale.setScalar(newScale);
      if (this.engine.requestRender) this.engine.requestRender();
      if (Math.abs(target - newScale) > target * 0.002) {
        this._scaleTransitionId = requestAnimationFrame(animate);
      } else {
        this.brushCursor.scale.setScalar(target);
        this._scaleTransitionId = null;
        if (this.engine.requestRender) this.engine.requestRender();
      }
    };
    this._scaleTransitionId = requestAnimationFrame(animate);
  }

  _hideBrushCursor() {
    this.brushCursor.visible = false;
    if (this._scaleTransitionId) {
      cancelAnimationFrame(this._scaleTransitionId);
      this._scaleTransitionId = null;
    }
  }

  // ---------- Start / Stop erasing mode ----------

  startErasing() {
    if (this.erasing) return;
    if (!this.enabled) return;

    // Re-capture mesh in case it changed
    this._captureTargetMesh();
    this._computeBrushRadius();
    this._buildSpatialGrid();

    this.erasing = true;
    this.activeSequenceId = null; // no active sequence until mousedown

    // Attach event listeners
    const canvas = this.engine.renderer.domElement;
    canvas.addEventListener('mousemove', this._onMouseMove);
    canvas.addEventListener('mousedown', this._onMouseDown);
    canvas.addEventListener('mouseup', this._onMouseUp);
    window.addEventListener('keydown', this._onKeyDown);

    // Disable orbit controls
    this.controls.enabled = false;

    // Cursor
    if (this.viewport) this.viewport.style.cursor = 'crosshair';
  }

  stopErasing() {
    if (!this.erasing) return;

    // Finalize any in-progress sequence
    this._finalizeActiveSequence();

    this.erasing = false;
    this.isMouseDown = false;

    // Detach event listeners
    const canvas = this.engine.renderer.domElement;
    canvas.removeEventListener('mousemove', this._onMouseMove);
    canvas.removeEventListener('mousedown', this._onMouseDown);
    canvas.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('keydown', this._onKeyDown);

    // Re-enable orbit controls
    this.controls.enabled = true;

    // Cursor
    if (this.viewport) this.viewport.style.cursor = '';

    // Hide brush
    this._hideBrushCursor();

    // Reset Start/Stop button
    const btn = document.getElementById('btnStartErasing');
    if (btn) {
      btn.textContent = 'Start Erasing';
      btn.style.background = '#c0392b';
    }

    // Update UI
    this.filters.renderEraserSequencesList();
    this._updateResultsDisplay();
  }

  // ---------- Mouse handlers ----------

  _handleMouseMove(event) {
    if (!this.erasing) return;

    // Capture mouse position immediately (cheap)
    const canvas = this.engine.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    this._mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this._mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Throttle the expensive raycast to once per animation frame
    if (this._mouseMoveScheduled) return;
    this._mouseMoveScheduled = true;
    requestAnimationFrame(() => {
      this._mouseMoveScheduled = false;
      if (!this.erasing) return;
      this._processMouseMove();
    });
  }

  _processMouseMove() {
    this.viewer.raycaster.setFromCamera(this._mouse, this.engine.camera);
    const meshObjects = this.viewer.getRaycastableObjects();
    const intersects = this.viewer.raycaster.intersectObjects(meshObjects, true);
    const visibleIntersects = intersects.filter(hit => this.viewer.isObjectFullyVisible(hit.object));

    if (visibleIntersects.length > 0) {
      const hit = visibleIntersects[0];
      this._lastHitDistance = hit.distance;
      this._showBrushCursor(hit.point);

      if (this.isMouseDown) {
        this._eraseBrushStroke(hit.point, hit.face?.normal);
      }
    } else if (this._lastHitDistance > 0) {
      // No mesh hit (e.g. triangles just erased under cursor).
      // Project the cursor along the camera ray at the last known distance
      // so it follows the mouse smoothly instead of flickering.
      const ray = this.viewer.raycaster.ray;
      const projected = ray.origin.clone().addScaledVector(ray.direction, this._lastHitDistance);
      this._showBrushCursor(projected);
    }

    // Request render for cursor movement
    if (this.engine.requestRender) this.engine.requestRender();
  }

  _handleMouseDown(event) {
    if (!this.erasing) return;
    if (event.button !== 0) return; // left click only
    event.preventDefault();

    this.isMouseDown = true;

    // Create a new sequence for this stroke
    this.sequenceIdCounter++;
    const seq = {
      id: `seq_${Date.now()}_${this.sequenceIdCounter}`,
      name: `Sequence ${this.sequences.length + 1}`,
      erasedTriangles: new Map(), // meshKey → Set<number>
      visible: true,
      color: this.colors[this.sequences.length % this.colors.length]
    };
    this.sequences.push(seq);
    this.activeSequenceId = seq.id;

    // Perform first erase at current position
    const canvas = this.engine.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    this._mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this._mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.viewer.raycaster.setFromCamera(this._mouse, this.engine.camera);
    const meshObjects = this.viewer.getRaycastableObjects();
    const intersects = this.viewer.raycaster.intersectObjects(meshObjects, true);
    const visibleIntersects = intersects.filter(hit => this.viewer.isObjectFullyVisible(hit.object));

    if (visibleIntersects.length > 0) {
      const firstHit = visibleIntersects[0];
      this._eraseBrushStroke(firstHit.point, firstHit.face?.normal);
    }
  }

  _handleMouseUp(event) {
    if (!this.erasing) return;
    if (event.button !== 0) return;

    this.isMouseDown = false;

    // Flush any pending rebuild
    if (this._rebuildDirty) {
      this._rebuildDirty = false;
      this._rebuildScheduled = false;
      this._rebuildIndexBuffer();
    }

    // Finalize the sequence (auto-delete if empty)
    this._finalizeActiveSequence();

    // Update UI and sync
    this.filters.renderEraserSequencesList();
    this._updateResultsDisplay();
    this._requestSync();
  }

  _handleKeyDown(event) {
    if (!this.erasing) return;
    if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
      event.preventDefault();
      this.undoLastSequence();
    }
  }

  _finalizeActiveSequence() {
    const seq = this._getActiveSequence();
    if (seq && this._getErasedTriCount(seq) === 0) {
      this.sequences = this.sequences.filter(s => s.id !== seq.id);
    }
    this.activeSequenceId = null;
  }

  // ---------- Erasing core ----------

  _eraseBrushStroke(hitPoint, hitFaceNormal) {
    if (this._isGroupMode()) {
      this._eraseBrushStrokeMulti(hitPoint, hitFaceNormal);
      return;
    }
    // Single mesh mode
    const mesh = this._getSingleMesh();
    if (!mesh || !mesh.geometry) return;

    const seq = this._getActiveSequence();
    if (!seq) return;

    const geo = mesh.geometry;
    const pos = geo.getAttribute('position');
    const idx = this.originalIndices;
    if (!idx) return;

    const meshKey = this._getMeshKey(mesh);

    // Transform hit point from world space to local (object) space
    const localHit = hitPoint.clone();
    mesh.updateMatrixWorld();
    mesh.worldToLocal(localHit);

    const radiusSq = this.brushRadius * this.brushRadius;
    const hx = localHit.x, hy = localHit.y, hz = localHit.z;
    let found = false;

    // Hit face normal in local space (used to reject back-facing triangles)
    const hasNormal = hitFaceNormal && (hitFaceNormal.x !== 0 || hitFaceNormal.y !== 0 || hitFaceNormal.z !== 0);
    const hnx = hasNormal ? hitFaceNormal.x : 0;
    const hny = hasNormal ? hitFaceNormal.y : 0;
    const hnz = hasNormal ? hitFaceNormal.z : 0;

    // Collect all already-erased triangles for this mesh
    const allErased = this._getAllErasedForMesh(meshKey);

    // Use spatial grid if available
    let candidates;
    if (this.spatialGrid && this.gridCellSize > 0) {
      candidates = this._getNearbyCells(hx, hy, hz);
    } else {
      candidates = [];
      for (let t = 0; t < idx.length / 3; t++) candidates.push(t);
    }

    for (const t of candidates) {
      if (allErased.has(t)) continue;

      const base = t * 3;
      const a = idx[base], b = idx[base + 1], c = idx[base + 2];

      // Triangle vertex positions
      const ax = pos.getX(a), ay = pos.getY(a), az = pos.getZ(a);
      const bx = pos.getX(b), by = pos.getY(b), bz = pos.getZ(b);
      const ccx = pos.getX(c), ccy = pos.getY(c), ccz = pos.getZ(c);

      // Centroid
      const cx = (ax + bx + ccx) / 3;
      const cy = (ay + by + ccy) / 3;
      const cz = (az + bz + ccz) / 3;

      // Distance check
      const dx = cx - hx, dy = cy - hy, dz = cz - hz;
      if (this.brushShape === 'cube') {
        const halfSize = this.brushRadius;
        if (Math.abs(dx) > halfSize || Math.abs(dy) > halfSize || Math.abs(dz) > halfSize) continue;
      } else {
        if (dx * dx + dy * dy + dz * dz > radiusSq) continue;
      }

      // Normal alignment check: only erase triangles facing roughly the same
      // direction as the surface the user clicked on. This prevents erasing
      // through thin walls to the opposite side.
      if (hasNormal) {
        const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
        const e2x = ccx - ax, e2y = ccy - ay, e2z = ccz - az;
        const fnx = e1y * e2z - e1z * e2y;
        const fny = e1z * e2x - e1x * e2z;
        const fnz = e1x * e2y - e1y * e2x;
        const dot = fnx * hnx + fny * hny + fnz * hnz;
        if (dot <= 0) continue;
      }

      this._addErasedTriangle(seq, meshKey, t);
      found = true;
    }

    if (found) {
      this._scheduleRebuild();
    }
  }

  _eraseBrushStrokeMulti(hitPoint, hitFaceNormal) {
    const seq = this._getActiveSequence();
    if (!seq) return;

    const meshes = this._getAllChildMeshes();
    if (meshes.length === 0) return;

    const radiusSq = this.brushRadius * this.brushRadius;
    let found = false;

    // NOTE: We intentionally skip the normal alignment check in multi-mesh mode.
    // In OpenFOAM Groups, overlapping child meshes (boundary patches) at the same
    // surface location can have opposite normals. The single-mesh normal check
    // prevents erasing through thin walls, but in multi-mesh mode it causes
    // back-facing patches to survive and show through as black triangles.
    // The distance/brush check is sufficient to limit the erased area.

    for (const mesh of meshes) {
      const meshKey = this._getMeshKey(mesh);
      const indices = this._getOriginalIndices(mesh);
      if (!indices || !mesh.geometry) continue;

      const pos = mesh.geometry.getAttribute('position');

      // Transform hit point to this mesh's local space
      const localHit = hitPoint.clone();
      mesh.updateMatrixWorld();
      mesh.worldToLocal(localHit);

      const hx = localHit.x, hy = localHit.y, hz = localHit.z;

      // Collect already-erased triangles for this mesh
      const allErased = this._getAllErasedForMesh(meshKey);

      // Use per-mesh spatial grid if available
      const grid = this.spatialGrids.get(meshKey);
      let candidates;
      if (grid && this.gridCellSize > 0) {
        candidates = this._getNearbyCells(hx, hy, hz, grid);
      } else {
        candidates = [];
        for (let t = 0; t < indices.length / 3; t++) candidates.push(t);
      }

      for (const t of candidates) {
        if (allErased.has(t)) continue;

        const base = t * 3;
        const a = indices[base], b = indices[base + 1], c = indices[base + 2];

        const ax = pos.getX(a), ay = pos.getY(a), az = pos.getZ(a);
        const bx = pos.getX(b), by = pos.getY(b), bz = pos.getZ(b);
        const ccx = pos.getX(c), ccy = pos.getY(c), ccz = pos.getZ(c);

        const cx = (ax + bx + ccx) / 3;
        const cy = (ay + by + ccy) / 3;
        const cz = (az + bz + ccz) / 3;

        const dx = cx - hx, dy = cy - hy, dz = cz - hz;
        if (this.brushShape === 'cube') {
          const halfSize = this.brushRadius;
          if (Math.abs(dx) > halfSize || Math.abs(dy) > halfSize || Math.abs(dz) > halfSize) continue;
        } else {
          if (dx * dx + dy * dy + dz * dz > radiusSq) continue;
        }

        this._addErasedTriangle(seq, meshKey, t);
        found = true;
      }
    }

    if (found) {
      this._scheduleRebuild();
    }
  }

  // ---------- Throttled index buffer rebuild ----------

  _scheduleRebuild() {
    this._rebuildDirty = true;
    if (this._rebuildScheduled) return;
    this._rebuildScheduled = true;
    requestAnimationFrame(() => {
      this._rebuildScheduled = false;
      if (this._rebuildDirty) {
        this._rebuildDirty = false;
        this._rebuildIndexBuffer();
      }
    });
  }

  _rebuildIndexBuffer() {
    if (this._isGroupMode()) {
      this._rebuildIndexBufferMulti();
      return;
    }
    // Single mesh mode
    const mesh = this._getSingleMesh();
    if (!mesh || !mesh.geometry) return;

    const meshKey = this._getMeshKey(mesh);
    const allErased = this._getAllErasedForMesh(meshKey);
    const original = this.originalIndices;
    if (!original) return;

    const totalTris = original.length / 3;
    const newIndices = [];

    for (let t = 0; t < totalTris; t++) {
      if (!allErased.has(t)) {
        const base = t * 3;
        newIndices.push(original[base], original[base + 1], original[base + 2]);
      }
    }

    const geo = mesh.geometry;
    const newArr = new Uint32Array(newIndices);
    geo.setIndex(new THREE.BufferAttribute(newArr, 1));
    geo.index.needsUpdate = true;
    geo.computeVertexNormals();
    // Three.js r128 bug: WebGLGeometries.getWireframeAttribute() doesn't
    // delete the wireframe cache entry when the index reference changes,
    // so the stale wireframe persists. Disposing forces full cache clear.
    geo.dispose();

    if (this.engine.requestRender) this.engine.requestRender();
  }

  _rebuildIndexBufferMulti() {
    const meshes = this._getAllChildMeshes();
    const allErasedMap = this._getAllErasedTrianglesMap();

    for (const mesh of meshes) {
      const meshKey = this._getMeshKey(mesh);
      const original = this.originalIndicesMap.get(meshKey);
      if (!original || !mesh.geometry) continue;

      const erasedSet = allErasedMap.get(meshKey);
      if (!erasedSet || erasedSet.size === 0) {
        // No erased triangles in this mesh — restore original
        mesh.geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(original), 1));
        mesh.geometry.index.needsUpdate = true;
        mesh.geometry.computeVertexNormals();
        mesh.geometry.dispose(); // r128 wireframe cache fix
        continue;
      }

      const totalTris = original.length / 3;
      const newIndices = [];
      for (let t = 0; t < totalTris; t++) {
        if (!erasedSet.has(t)) {
          const base = t * 3;
          newIndices.push(original[base], original[base + 1], original[base + 2]);
        }
      }

      mesh.geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(newIndices), 1));
      mesh.geometry.index.needsUpdate = true;
      mesh.geometry.computeVertexNormals();
      mesh.geometry.dispose(); // r128 wireframe cache fix
    }

    if (this.engine.requestRender) this.engine.requestRender();
  }

  /**
   * Get all erased triangles across all visible sequences, as a Map of meshKey → Set<number>
   */
  _getAllErasedTrianglesMap() {
    const allMap = new Map();
    for (const seq of this.sequences) {
      if (seq.visible) {
        for (const [meshKey, triSet] of seq.erasedTriangles) {
          if (!allMap.has(meshKey)) allMap.set(meshKey, new Set());
          const target = allMap.get(meshKey);
          for (const t of triSet) target.add(t);
        }
      }
    }
    return allMap;
  }

  /**
   * Get all erased triangles for a specific mesh across all visible sequences
   */
  _getAllErasedForMesh(meshKey) {
    const all = new Set();
    for (const seq of this.sequences) {
      if (seq.visible) {
        const triSet = seq.erasedTriangles.get(meshKey);
        if (triSet) {
          for (const t of triSet) all.add(t);
        }
      }
    }
    return all;
  }

  /**
   * Get total erased triangle count across all meshes in a sequence
   */
  _getErasedTriCount(seq) {
    let count = 0;
    for (const set of seq.erasedTriangles.values()) count += set.size;
    return count;
  }

  /**
   * Get total erased triangle count across all visible sequences
   */
  _getTotalErasedCount() {
    const allMap = this._getAllErasedTrianglesMap();
    let count = 0;
    for (const set of allMap.values()) count += set.size;
    return count;
  }

  /**
   * Add a triangle to the erased set for a specific mesh in a sequence
   */
  _addErasedTriangle(seq, meshKey, triIndex) {
    if (!seq.erasedTriangles.has(meshKey)) {
      seq.erasedTriangles.set(meshKey, new Set());
    }
    seq.erasedTriangles.get(meshKey).add(triIndex);
  }

  // ---------- Undo (removes last sequence) ----------

  undoLastSequence() {
    if (this.sequences.length === 0) return;

    // Remove the most recent sequence
    this.sequences.pop();
    this._rebuildIndexBuffer();
    this.filters.renderEraserSequencesList();
    this._updateResultsDisplay();
    this._requestSync();
  }

  // ---------- Sequence management ----------

  toggleSequence(seqId) {
    const seq = this.sequences.find(s => s.id === seqId);
    if (!seq) return;
    seq.visible = !seq.visible;
    this._rebuildIndexBuffer();
    this.filters.renderEraserSequencesList();
    this._updateResultsDisplay();
    this._requestSync();
  }

  deleteSequence(seqId) {
    this.sequences = this.sequences.filter(s => s.id !== seqId);
    this._rebuildIndexBuffer();
    this.filters.renderEraserSequencesList();
    this._updateResultsDisplay();
    this._requestSync();
  }

  renameSequence(seqId, newName) {
    const seq = this.sequences.find(s => s.id === seqId);
    if (seq) seq.name = newName.trim() || seq.name;
  }

  clearAllSequences() {
    if (this.erasing) this.stopErasing();
    this.sequences = [];
    this.activeSequenceId = null;

    // Restore original indices for all meshes
    if (this._isGroupMode()) {
      const meshes = this._getAllChildMeshes();
      for (const mesh of meshes) {
        const key = this._getMeshKey(mesh);
        const original = this.originalIndicesMap.get(key);
        if (original && mesh.geometry) {
          mesh.geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(original), 1));
          mesh.geometry.index.needsUpdate = true;
          mesh.geometry.computeVertexNormals();
          mesh.geometry.dispose(); // r128 wireframe cache fix
        }
      }
    } else {
      const mesh = this._getSingleMesh();
      if (mesh && mesh.geometry && this.originalIndices) {
        const geo = mesh.geometry;
        geo.setIndex(new THREE.BufferAttribute(new Uint32Array(this.originalIndices), 1));
        geo.index.needsUpdate = true;
        geo.computeVertexNormals();
        geo.dispose(); // r128 wireframe cache fix
      }
    }

    if (this.engine.requestRender) this.engine.requestRender();

    this.filters.renderEraserSequencesList();
    this._updateResultsDisplay();
    this._requestSync();
  }

  // ---------- Collaboration sync ----------

  _requestSync() {
    const vlm = this.viewer.viewLockManager || window.viewLockManager;
    if (vlm && vlm.amIController) {
      vlm.onVisualizationChange();
    }
  }

  // ---------- Helpers ----------

  _getActiveSequence() {
    return this.sequences.find(s => s.id === this.activeSequenceId) || null;
  }

  _getSingleMesh() {
    if (!this.targetMesh) return null;
    if (this.targetMesh.isMesh) return this.targetMesh;
    let first = null;
    this.targetMesh.traverse(child => {
      if (!first && child.isMesh) first = child;
    });
    return first;
  }

  _getAllChildMeshes() {
    if (!this.targetMesh) return [];
    if (this.targetMesh.isMesh) return [this.targetMesh];
    const meshes = [];
    this.targetMesh.traverse(child => {
      if (child.isMesh) meshes.push(child);
    });
    return meshes;
  }

  _getMeshKey(mesh) {
    return mesh.name || mesh.uuid;
  }

  _isGroupMode() {
    return this.targetMesh != null && !this.targetMesh.isMesh;
  }

  _getOriginalIndices(mesh) {
    if (this._isGroupMode()) {
      return this.originalIndicesMap.get(this._getMeshKey(mesh));
    }
    return this.originalIndices;
  }

  _updateResultsDisplay() {
    const resultsDiv = document.getElementById('meshEraserResults');
    const trisSpan = document.getElementById('eraserResultTriangles');
    const seqsSpan = document.getElementById('eraserResultSequences');
    if (!resultsDiv) return;

    const totalErased = this._getTotalErasedCount();
    if (totalErased > 0 || this.sequences.length > 0) {
      resultsDiv.style.display = '';
      if (trisSpan) trisSpan.textContent = totalErased.toLocaleString();
      if (seqsSpan) seqsSpan.textContent = this.sequences.length;
    } else {
      resultsDiv.style.display = 'none';
    }
  }

  // ---------- Dispose ----------

  dispose() {
    if (this.erasing) this.stopErasing();
    if (this.brushCursor) {
      this.engine.scene.remove(this.brushCursor);
      this.brushCursor.geometry.dispose();
      this.brushCursor.material.dispose();
      this.brushCursor = null;
    }
    // Restore original indices for all meshes
    if (this._isGroupMode()) {
      const meshes = this._getAllChildMeshes();
      for (const mesh of meshes) {
        const key = this._getMeshKey(mesh);
        const original = this.originalIndicesMap.get(key);
        if (original && mesh.geometry) {
          mesh.geometry.setIndex(new THREE.BufferAttribute(new Uint32Array(original), 1));
          mesh.geometry.index.needsUpdate = true;
          mesh.geometry.computeVertexNormals();
          mesh.geometry.dispose(); // r128 wireframe cache fix
        }
      }
    } else {
      const mesh = this._getSingleMesh();
      if (mesh && mesh.geometry && this.originalIndices) {
        const geo = mesh.geometry;
        geo.setIndex(new THREE.BufferAttribute(new Uint32Array(this.originalIndices), 1));
        geo.index.needsUpdate = true;
        geo.computeVertexNormals();
        geo.dispose(); // r128 wireframe cache fix
      }
    }
  }
}
